/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import assetCache from '../utils/assetCache';

/**
 * Renders the live network URL first (so it benefits from normal HTTP
 * caching and always shows the freshest asset while online), and only
 * swaps to a locally cached blob when the element's own onError fires
 * (offline, or a reload with nothing left in the browser cache).
 *
 * Safe to use on blob: URLs (e.g. not-yet-uploaded local captures) — those
 * never error, so tryFallback simply never engages for them.
 */
const useCachedAssetSrc = (token, url) => {
  const [src, setSrc] = useState(url);
  const objectUrlRef = useRef(null);
  const usingFallbackRef = useRef(false);

  useEffect(() => {
    setSrc(url);
    usingFallbackRef.current = false;
  }, [url]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [url]);

  // Returns true if it swapped to a cached blob (caller should stop there);
  // false if there's nothing to fall back to (caller shows its own placeholder).
  const tryFallback = useCallback(async () => {
    if (!token || !url || usingFallbackRef.current) return false;
    const blob = await assetCache.getCachedAssetBlob(token, url);
    if (!blob) return false;
    const objectUrl = URL.createObjectURL(blob);
    objectUrlRef.current = objectUrl;
    usingFallbackRef.current = true;
    setSrc(objectUrl);
    return true;
  }, [token, url]);

  return { src, tryFallback };
};

export default useCachedAssetSrc;
